import React from "react";
import IconArrowDown from "../../SliderView/Icons/IconArrowDown";
import { profileImageRails } from "./profileImage";
import "./ProfileImageRail.css";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export class ProfileImageRail extends React.Component {
  state = { canScrollPrev: false, canScrollNext: false };

  trackRef = React.createRef();

  componentDidMount() {
    this.updateScrollState();
    window.addEventListener("resize", this.updateScrollState);
    if (typeof ResizeObserver !== "undefined" && this.trackRef.current) {
      this.resizeObserver = new ResizeObserver(this.updateScrollState);
      this.resizeObserver.observe(this.trackRef.current);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.rail !== this.props.rail) this.updateScrollState();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateScrollState);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  updateScrollState = () => {
    const track = this.trackRef.current;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const canScrollPrev = track.scrollLeft > 1;
    const canScrollNext = track.scrollLeft < maxScroll - 1;

    if (
      canScrollPrev !== this.state.canScrollPrev ||
      canScrollNext !== this.state.canScrollNext
    ) {
      this.setState({ canScrollPrev, canScrollNext });
    }
  };

  scrollByPage = (direction) => {
    const track = this.trackRef.current;
    if (!track) return;

    const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
    const distance = Math.max(track.clientWidth * 0.8, 1) * direction;
    const target = Math.min(Math.max(track.scrollLeft + distance, 0), maxScroll);
    const delta = target - track.scrollLeft;
    if (!delta) return;

    if (typeof track.scrollBy === "function") {
      track.scrollBy({ left: delta, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    } else {
      track.scrollLeft = target;
    }
  };

  renderArrow(type) {
    const { rail } = this.props;
    const direction = type === "prev" ? -1 : 1;

    return (
      <button
        type="button"
        className={`profile-image-rail__arrow profile-image-rail__arrow--${type}`}
        aria-label={`Scroll ${rail.name} ${type === "prev" ? "left" : "right"}`}
        tabIndex={-1}
        onClick={() => this.scrollByPage(direction)}
      >
        <span aria-hidden="true">
          <IconArrowDown />
        </span>
      </button>
    );
  }

  render() {
    const { rail, selectedId, onSelect, idPrefix } = this.props;
    const { canScrollPrev, canScrollNext } = this.state;
    const headingId = `${idPrefix}-profile-rail-${rail.id}`;

    return (
      <section className="profile-image-rail" aria-labelledby={headingId}>
        <div className="edit-profile-img-title-sec profile-image-rail__header">
          <h4 id={headingId}>{rail.name}</h4>
        </div>
        <div
          className={
            canScrollPrev || canScrollNext
              ? "profile-image-rail__viewport profile-image-rail__viewport--overflow"
              : "profile-image-rail__viewport"
          }
        >
          {canScrollPrev ? this.renderArrow("prev") : null}
          <div
            className="profile-image-rail__track"
            ref={this.trackRef}
            onScroll={this.updateScrollState}
          >
            {rail.thumbnails.map((thumbnail, index) => {
              const inputId = `${idPrefix}-profile-thumbnail-${thumbnail.id}`;
              return (
                <div
                  className="edit-profile-img-card profile-image-rail__card"
                  key={thumbnail.id}
                >
                  <input
                    type="radio"
                    name="thumbnail_id"
                    id={inputId}
                    value={thumbnail.id}
                    checked={String(selectedId) === String(thumbnail.id)}
                    onChange={onSelect}
                  />
                  <label htmlFor={inputId}>
                    <span className="profile-image-rail__spotlight" aria-hidden="true" />
                    <span className="profile-image-rail__shape" aria-hidden="true" />
                    <span className="profile-image-rail__frame">
                      <img
                        className="profile-image-rail__img"
                        src={thumbnail.picture}
                        alt={`${rail.name} avatar ${index + 1}`}
                        loading="lazy"
                        draggable="false"
                      />
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
          {canScrollNext ? this.renderArrow("next") : null}
        </div>
      </section>
    );
  }
}

const ProfileImageRails = ({ categories, selectedId, onSelect, idPrefix }) => {
  const rails = profileImageRails(categories);
  if (!rails.length) return null;

  return (
    <div className="profile-image-rails">
      {rails.map((rail) => (
        <ProfileImageRail
          key={rail.id}
          rail={rail}
          selectedId={selectedId}
          onSelect={onSelect}
          idPrefix={idPrefix}
        />
      ))}
    </div>
  );
};

export default ProfileImageRails;
